import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  api,
} from "@/lib/api";
import UploadScenePromptsButton from "@/components/UploadScenePromptsButton";
import JobProgressBanner from "@/components/JobProgressBanner";
import VideoProviderSelect from "@/components/VideoProviderSelect";
import ProviderShootoutModal from "@/components/ProviderShootoutModal";

const MAX_SCENES = 20;
const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Roll Your Own — manual upload + click-to-animate per Active Channel.
 *
 * Differs from /scene-to-video (which auto-serial-animates on submit)
 * in two key ways:
 *   1. Job is scoped to a single Active Channel (channel_id on the doc)
 *   2. User clicks Animate per scene OR "Animate All" — nothing fires
 *      automatically on submit. Matches the Idea Bank Scene Approval
 *      List UX so users feel at home.
 */
export default function RollYourOwn() {
  const { channelId } = useParams();

  // --- Uploader form state ---
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [defaultPrompt, setDefaultPrompt] = useState(
    "Smooth, natural motion. Cinematic camera move. "
    + "Keep all subjects visually consistent with the input image.",
  );
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  // Parsed scene descriptions from an uploaded doc, kept alive so the
  // user can re-map them to a different starting scene number.
  const [parsedDocPrompts, setParsedDocPrompts] = useState([]);
  const [descStartScene, setDescStartScene] = useState(1);
  const [videoProvider, setVideoProvider] = useState("");
  const [shootoutOpen, setShootoutOpen] = useState(false);
  const [shootoutSceneIdx, setShootoutSceneIdx] = useState(0);

  // --- Job + animation state ---
  const [job, setJob] = useState(null);
  const [animatingScenes, setAnimatingScenes] = useState(() => new Set());
  const [animateAllBusy, setAnimateAllBusy] = useState(false);
  const animateAllAbortRef = useRef(false);
  const pollTimerRef = useRef(null);

  // --- Helpers ---
  const markAnimating = (idx, on) => {
    setAnimatingScenes((prev) => {
      const next = new Set(prev);
      if (on) next.add(idx);
      else next.delete(idx);
      return next;
    });
  };

  const fetchActiveJob = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/active-channels/${channelId}/roll-your-own/jobs/active`,
      );
      setJob(data?.job || null);
      return data?.job || null;
    } catch (err) {
      console.debug("RYO active fetch failed:", err?.message);
      return null;
    }
  }, [channelId]);

  const fetchJob = useCallback(async (jid) => {
    try {
      const { data } = await api.get(
        `/active-channels/${channelId}/roll-your-own/jobs/${jid}`,
        { params: { _t: Date.now() } },
      );
      if (data?.job) setJob(data.job);
      return data?.job || null;
    } catch (err) {
      console.debug("RYO job fetch failed:", err?.message);
      return null;
    }
  }, [channelId]);

  const startBackgroundPoll = useCallback((jid) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(async () => {
      const j = await fetchJob(jid);
      // Stop polling once nothing is in flight server-side AND no
      // local Animate-All loop is running.
      const inFlight =
        j?.scenes?.some(
          (s) => s.status === "queued" || s.status === "animating",
        ) || false;
      if (!inFlight && !animateAllAbortRef.current) {
        // Keep polling for a few more cycles if a click-to-animate
        // is in progress (local animatingScenes set).
        const localPending =
          animatingScenes.size > 0 || animateAllBusy;
        if (!localPending) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      }
    }, 4000);
  }, [fetchJob, animatingScenes.size, animateAllBusy]);

  useEffect(() => {
    fetchActiveJob();
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [fetchActiveJob]);

  useEffect(() => {
    if (job?.job_id) startBackgroundPoll(job.job_id);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [job?.job_id, startBackgroundPoll]);

  useEffect(() => {
    return () => {
      previews.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { /* noop */ }
      });
    };
  }, [previews]);

  // --- File picker ---
  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []).slice(0, MAX_SCENES);
    if (picked.length === 0) return;
    if (files.length + picked.length > MAX_SCENES) {
      toast.error(`Max ${MAX_SCENES} stills per job`);
      e.target.value = "";
      return;
    }
    setFiles((arr) => [...arr, ...picked]);
    setPreviews((arr) => [
      ...arr,
      ...picked.map((f) => URL.createObjectURL(f)),
    ]);
    setPrompts((arr) => [...arr, ...picked.map(() => "")]);
    e.target.value = "";
  };

  const removeScene = (idx) => {
    setFiles((arr) => arr.filter((_, i) => i !== idx));
    setPreviews((arr) => {
      try { URL.revokeObjectURL(arr[idx]); } catch { /* noop */ }
      return arr.filter((_, i) => i !== idx);
    });
    setPrompts((arr) => arr.filter((_, i) => i !== idx));
  };

  const moveScene = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= files.length) return;
    const swap = (arr) => {
      const next = [...arr];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    };
    setFiles(swap);
    setPreviews(swap);
    setPrompts(swap);
  };

  const clearAll = () => {
    previews.forEach((u) => {
      try { URL.revokeObjectURL(u); } catch { /* noop */ }
    });
    setFiles([]);
    setPreviews([]);
    setPrompts([]);
    setTitle("");
    setUploadPct(0);
  };

  // --- Submit (upload only, no animate) ---
  const submit = async () => {
    if (files.length === 0) return;
    setSubmitting(true);
    setUploadPct(0);
    const form = new FormData();
    for (const f of files) form.append("images", f);
    for (const p of prompts) form.append("prompts", p || "");
    form.append("default_prompt", defaultPrompt || "");
    form.append("title", title || "");
    if (videoProvider) form.append("video_provider", videoProvider);
    try {
      const { data } = await api.post(
        `/active-channels/${channelId}/roll-your-own/jobs`,
        form,
        {
          timeout: 5 * 60 * 1000,
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
          },
        },
      );
      toast.success(`Job ${data.job_id} ready — click Animate to begin`);
      clearAll();
      await fetchActiveJob();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || "Upload failed",
      );
    } finally {
      setSubmitting(false);
      setUploadPct(0);
    }
  };

  // --- Animate single scene ---
  const waitForSceneReady = useCallback(
    async (jid, idx, { maxMs = 6 * 60 * 1000, intervalMs = 5000 } = {}) => {
      const deadline = Date.now() + maxMs;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, intervalMs));
        const j = await fetchJob(jid);
        const sc = (j?.scenes || [])[idx];
        if (!sc) continue;
        if (sc.status === "ready") return { ok: true };
        if (sc.status === "failed") {
          return { ok: false, error: sc.error || "Veo 3 failed" };
        }
      }
      return { ok: false, error: "Timed out" };
    },
    [fetchJob],
  );

  const handleAnimateScene = async (idx) => {
    if (!job?.job_id) return;
    markAnimating(idx, true);
    const tid = toast.loading(`Sending scene ${idx + 1} to Veo 3…`);
    try {
      await api.post(
        `/active-channels/${channelId}/roll-your-own/jobs/${job.job_id}`
        + `/scenes/${idx}/animate`,
      );
      toast.loading(`Scene ${idx + 1} animating (8s)…`, { id: tid });
      await fetchJob(job.job_id);
      const r = await waitForSceneReady(job.job_id, idx);
      if (r.ok) toast.success(`Scene ${idx + 1} animated`, { id: tid });
      else toast.error(`Scene ${idx + 1}: ${r.error}`, { id: tid });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || `Couldn't animate scene ${idx + 1}`,
        { id: tid },
      );
    } finally {
      markAnimating(idx, false);
    }
  };

  // --- Animate All ---
  const handleAnimateAll = async () => {
    if (!job?.job_id) return;
    const scenes = job.scenes || [];
    const queue = scenes
      .filter((s) => s.status !== "ready" && s.status !== "animating")
      .map((s) => s.idx);
    if (queue.length === 0) {
      toast.info("No scenes left to animate");
      return;
    }
    setAnimateAllBusy(true);
    animateAllAbortRef.current = false;
    let okCount = 0;
    let failCount = 0;
    for (const idx of queue) {
      if (animateAllAbortRef.current) break;
      markAnimating(idx, true);
      try {
        await api.post(
          `/active-channels/${channelId}/roll-your-own/jobs/${job.job_id}`
          + `/scenes/${idx}/animate`,
        );
        await fetchJob(job.job_id);
        const r = await waitForSceneReady(job.job_id, idx);
        if (r.ok) okCount += 1;
        else {
          failCount += 1;
          toast.error(`Scene ${idx + 1}: ${r.error}`);
        }
      } catch (err) {
        failCount += 1;
        toast.error(
          err?.response?.data?.detail || `Scene ${idx + 1} failed to start`,
        );
      } finally {
        markAnimating(idx, false);
      }
    }
    setAnimateAllBusy(false);
    if (okCount > 0) {
      toast.success(
        `Animated ${okCount}/${queue.length} scene${queue.length === 1 ? "" : "s"}`,
      );
    }
    if (failCount > 0 && okCount === 0) {
      toast.error(`All ${failCount} animation${failCount === 1 ? "" : "s"} failed`);
    }
  };

  const handleStopAnimateAll = () => {
    animateAllAbortRef.current = true;
    toast.info("Stopping after current scene finishes…");
  };

  // ---- Stitch state ----
  const [selectedForStitch, setSelectedForStitch] = useState(() => new Set());
  const [voiceoverFile, setVoiceoverFile] = useState(null);
  const [stitchBusy, setStitchBusy] = useState(false);

  // Track which scene idx have ALREADY been seen as ready so we only
  // auto-select each scene ONCE (on the first transition to ready).
  // After that, the user's manual un-check sticks across polls.
  const seenReadyRef = useRef(new Set());

  // Keep the stitch selection in sync with the live job state:
  //   • drop any scenes that are no longer ready (e.g. re-animate
  //     in flight) so the count is never stale.
  //   • auto-add scenes whose `status` just transitioned ready.
  //   • respect manual un-checks across polls.
  //
  // CRITICAL: all ref mutations happen BEFORE the setState call so the
  // updater stays pure. React.StrictMode double-invokes updaters to
  // surface impure ones — mutating seenReadyRef inside the updater
  // breaks under StrictMode because the second pass sees the already-
  // mutated ref and skips the auto-add.
  useEffect(() => {
    if (!job?.scenes) return;
    const readyIdxs = job.scenes
      .filter((s) => s.status === "ready").map((s) => s.idx);
    const readySet = new Set(readyIdxs);

    // Compute the set of scenes about to auto-select BEFORE touching
    // the ref. The list is captured into the updater closure below.
    const newlyReady = readyIdxs.filter(
      (idx) => !seenReadyRef.current.has(idx),
    );

    // Apply ref mutations exactly once (NOT inside the updater).
    for (const idx of newlyReady) seenReadyRef.current.add(idx);
    for (const idx of [...seenReadyRef.current]) {
      if (!readySet.has(idx)) seenReadyRef.current.delete(idx);
    }

    setSelectedForStitch((prev) => {
      const next = new Set(prev);
      for (const idx of prev) {
        if (!readySet.has(idx)) next.delete(idx);
      }
      for (const idx of newlyReady) next.add(idx);
      return next;
    });
  }, [job?.scenes]);

  const toggleStitchSelection = (idx) => {
    setSelectedForStitch((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleStitch = async () => {
    if (!job?.job_id) return;
    const orderedIndices = (job.scenes || [])
      .filter((s) => s.status === "ready" && selectedForStitch.has(s.idx))
      .map((s) => s.idx);
    if (orderedIndices.length === 0) {
      toast.error("Pick at least one ready scene to stitch");
      return;
    }
    setStitchBusy(true);
    const tid = toast.loading("Stitching selected clips…");
    try {
      const form = new FormData();
      form.append("scene_indices", orderedIndices.join(","));
      if (voiceoverFile) form.append("voiceover", voiceoverFile);
      await api.post(
        `/active-channels/${channelId}/roll-your-own/jobs/${job.job_id}`
        + `/stitch`,
        form,
        {
          timeout: 60_000,
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      await fetchJob(job.job_id);
      // Poll until stitched_status flips to ready/failed (12 min max).
      const deadline = Date.now() + 12 * 60 * 1000;
      let finalState = "running";
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        const j = await fetchJob(job.job_id);
        const st = j?.stitched_status || "";
        if (st === "ready" || st === "failed") {
          finalState = st;
          if (st === "failed") {
            toast.error(j?.stitched_error || "Stitch failed", { id: tid });
          } else {
            toast.success("Stitched video ready", { id: tid });
            // Clear the staged voiceover; user can upload another if
            // they re-stitch.
            setVoiceoverFile(null);
          }
          break;
        }
      }
      if (finalState === "running") {
        toast.error("Stitch timed out — check back in a minute", { id: tid });
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't start stitch", { id: tid },
      );
    } finally {
      setStitchBusy(false);
    }
  };

  // --- Derived ---
  const scenes = job?.scenes || [];
  const readyCount = scenes.filter((s) => s.status === "ready").length;
  const pendingAnim = scenes.filter(
    (s) => s.status !== "ready" && s.status !== "animating",
  ).length;

  return (
    <main
      data-testid="roll-your-own-page"
      className="px-6 py-8 max-w-6xl mx-auto space-y-10"
    >
      <header className="space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400">
          // ROLL YOUR OWN
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">
          Your stills. Your timing.
        </h1>
        <p className="text-sm text-zinc-400 max-w-2xl leading-relaxed">
          Upload up to 20 still images for this channel. Click ▶ Animate
          on individual scenes or ▶▶ Animate All to fire them sequentially
          through Veo 3 → 8-second clips.
        </p>
        <div className="pt-2">
          <Link
            to={`/active-channels/${channelId}`}
            data-testid="ryo-back-link"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-yellow-400 transition-colors"
          >
            ← Back to channel
          </Link>
        </div>
      </header>

      {!job && (
        <Uploader
          files={files}
          previews={previews}
          prompts={prompts}
          defaultPrompt={defaultPrompt}
          setDefaultPrompt={setDefaultPrompt}
          title={title}
          setTitle={setTitle}
          onPick={onPickFiles}
          onRemove={removeScene}
          onMove={moveScene}
          onClear={clearAll}
          setPromptAt={(i, v) => setPrompts((arr) => {
            const next = [...arr]; next[i] = v; return next;
          })}
          onPromptsFromDoc={(parsed) => {
            setParsedDocPrompts(parsed);
            // Auto-detect starting scene from the first filename.
            let detectedStart = 1;
            const first = files[0]?.name || "";
            const m = first.match(/scene[\s_\-]*0*(\d{1,3})/i);
            if (m) detectedStart = Math.max(1, Number(m[1]));
            setDescStartScene(detectedStart);
            setPrompts((arr) => {
              const n = Math.max(arr.length, parsed.length);
              const next = Array(n).fill("");
              for (let i = 0; i < n; i += 1) {
                const srcIdx = detectedStart - 1 + i;
                next[i] = srcIdx >= 0 && srcIdx < parsed.length
                  ? parsed[srcIdx]
                  : (arr[i] || "");
              }
              return next;
            });
          }}
          parsedDocPrompts={parsedDocPrompts}
          descStartScene={descStartScene}
          onChangeDescStartScene={(n) => {
            const start = Math.max(
              1,
              Math.min(parsedDocPrompts.length || 1, Number(n) || 1),
            );
            setDescStartScene(start);
            if (parsedDocPrompts.length === 0) return;
            setPrompts((arr) => {
              const total = Math.max(arr.length, files.length);
              const next = Array(total).fill("");
              for (let i = 0; i < total; i += 1) {
                const srcIdx = start - 1 + i;
                next[i] = srcIdx >= 0 && srcIdx < parsedDocPrompts.length
                  ? parsedDocPrompts[srcIdx]
                  : (arr[i] || "");
              }
              return next;
            });
          }}
          onSubmit={submit}
          submitting={submitting}
          uploadPct={uploadPct}
          videoProvider={videoProvider}
          onChangeVideoProvider={setVideoProvider}
        />
      )}

      {job && (
        <JobView
          job={job}
          scenes={scenes}
          channelId={channelId}
          animatingScenes={animatingScenes}          animateAllBusy={animateAllBusy}
          pendingAnim={pendingAnim}
          readyCount={readyCount}
          onAnimate={handleAnimateScene}
          onAnimateAll={handleAnimateAll}
          onStopAnimateAll={handleStopAnimateAll}
          onStartOver={() => { setJob(null); }}
          selectedForStitch={selectedForStitch}
          onToggleStitchSelection={toggleStitchSelection}
          voiceoverFile={voiceoverFile}
          onSetVoiceoverFile={setVoiceoverFile}
          stitchBusy={stitchBusy}
          onStitch={handleStitch}
        />
      )}

      {files.length > 0 && (
        <RyoShootoutLaunchCard
          files={files}
          previews={previews}
          prompts={prompts}
          defaultPrompt={defaultPrompt}
          sceneIdx={shootoutSceneIdx}
          onChangeSceneIdx={setShootoutSceneIdx}
          onOpen={() => setShootoutOpen(true)}
          disabled={submitting}
        />
      )}
      <ProviderShootoutModal
        open={shootoutOpen}
        onClose={() => setShootoutOpen(false)}
        sourceImageUrl={previews[shootoutSceneIdx] || ""}
        sourcePrompt={
          (prompts[shootoutSceneIdx] || "").trim() || defaultPrompt
        }
        onPick={(pid) => setVideoProvider(pid)}
      />
    </main>
  );
}

function RyoShootoutLaunchCard({
  files, previews, prompts, defaultPrompt, sceneIdx,
  onChangeSceneIdx, onOpen, disabled,
}) {
  const safeIdx = Math.max(0, Math.min(files.length - 1, sceneIdx));
  const previewUrl = previews[safeIdx];
  const promptPreview = (prompts[safeIdx] || "").trim() || defaultPrompt;
  return (
    <section
      data-testid="ryo-shootout-card"
      className="border border-yellow-400/40 bg-yellow-400/[0.03] rounded-sm p-5 space-y-3"
    >
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
            // PROVIDER SHOOTOUT
          </div>
          <div className="font-display text-2xl font-black text-white tracking-tight">
            Test one scene on every model
          </div>
          <p className="text-xs text-zinc-400 max-w-2xl leading-relaxed mt-1">
            Animates a single still through Veo 3, Kling 2.5, and Seedance 2.0
            in parallel — pick the winner and lock it in for the rest of the job.
          </p>
        </div>
        <button
          type="button"
          data-testid="ryo-shootout-open"
          onClick={onOpen}
          disabled={disabled}
          className="font-display text-sm font-black uppercase tracking-wide bg-yellow-400 text-black hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed px-5 py-2.5 rounded-sm"
        >
          ▶ Run shootout
        </button>
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        <div className="space-y-1">
          <label
            htmlFor="ryo-shootout-scene"
            className="block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500"
          >
            Test scene
          </label>
          <select
            id="ryo-shootout-scene"
            data-testid="ryo-shootout-scene"
            value={safeIdx}
            onChange={(e) => onChangeSceneIdx(Number(e.target.value))}
            disabled={disabled}
            className="bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-xs text-zinc-100"
          >
            {files.map((f, i) => (
              <option key={i} value={i}>
                Scene #{i + 1} — {(f.name || "").slice(0, 40)}
              </option>
            ))}
          </select>
        </div>
        {previewUrl && (
          <div className="flex items-center gap-3">
            <img
              src={previewUrl}
              alt={`Scene ${safeIdx + 1}`}
              className="w-32 aspect-video object-cover border border-zinc-800 rounded-sm"
            />
            <div className="text-[11px] text-zinc-400 max-w-md line-clamp-3 italic">
              “{promptPreview || "(no prompt set — default will be used)"}”
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- Uploader (no auto-animate) ----------
function Uploader({
  files, previews, prompts, defaultPrompt, setDefaultPrompt,
  title, setTitle, onPick, onRemove, onMove, onClear, setPromptAt,
  onPromptsFromDoc,
  parsedDocPrompts = [],
  descStartScene = 1,
  onChangeDescStartScene,
  videoProvider = "",
  onChangeVideoProvider,
  onSubmit, submitting, uploadPct,
}) {
  return (
    <section
      data-testid="ryo-uploader"
      className="border border-zinc-800 rounded-sm p-5 bg-black space-y-5"
    >
      {submitting && files.length > 0 && (
        <JobProgressBanner
          stage="uploading"
          uploadPct={uploadPct}
          total={files.length}
          testIdPrefix="ryo-upload-progress"
          jobLabel={`${files.length} still${files.length === 1 ? "" : "s"} · ${title || "untitled"}`}
        />
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            // STEP 1 · UPLOAD STILLS
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-0.5">
            {files.length}/{MAX_SCENES} added
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <UploadScenePromptsButton
            testIdPrefix="ryo-upload-prompts"
            onPrompts={(parsed) => onPromptsFromDoc?.(parsed)}
            disabled={submitting}
            label="Upload scene descriptions"
          />
          <label
            className={`font-mono text-[10px] uppercase tracking-[0.25em] cursor-pointer text-yellow-300 hover:text-yellow-200 border border-yellow-400/50 hover:border-yellow-400 px-3 py-2 rounded-sm ${
              submitting || files.length >= MAX_SCENES ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            + Add stills
            <input
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={onPick}
              data-testid="ryo-file-input"
              disabled={submitting || files.length >= MAX_SCENES}
            />
          </label>
          {files.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              disabled={submitting}
              data-testid="ryo-clear-btn"
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-red-300 border border-zinc-800 hover:border-red-500/50 px-3 py-2 rounded-sm"
            >
              × Clear
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[240px]">
          <label className="block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            JOB TITLE (optional)
          </label>
          <input
            data-testid="ryo-title-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 200))}
            placeholder="e.g. Birthday montage — scenes 1–8"
            disabled={submitting}
            className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-sm text-zinc-100"
          />
        </div>
        <VideoProviderSelect
          testIdPrefix="ryo-video-provider"
          value={videoProvider}
          onChange={onChangeVideoProvider}
          disabled={submitting}
        />
      </div>

      <div>
        <label className="block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
          DEFAULT VEO 3 PROMPT — applied to every scene unless overridden below
        </label>
        <textarea
          data-testid="ryo-default-prompt"
          value={defaultPrompt}
          onChange={(e) => setDefaultPrompt(e.target.value.slice(0, 1000))}
          disabled={submitting}
          rows={2}
          className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-xs text-zinc-100 leading-relaxed"
        />
      </div>

      {parsedDocPrompts.length > 0 && (
        <div
          data-testid="ryo-scene-offset-bar"
          className="flex flex-wrap items-center gap-3 border border-yellow-400/30 bg-yellow-400/5 rounded-sm px-3 py-2"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-300 font-bold">
            ● {parsedDocPrompts.length} scene description{parsedDocPrompts.length === 1 ? "" : "s"} loaded
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 flex items-center gap-2">
            <label htmlFor="ryo-scene-offset">
              First still = source Scene #
            </label>
            <input
              id="ryo-scene-offset"
              data-testid="ryo-scene-offset-input"
              type="number"
              min={1}
              max={parsedDocPrompts.length}
              value={descStartScene}
              onChange={(e) => onChangeDescStartScene?.(e.target.value)}
              disabled={submitting}
              className="w-16 bg-black border border-yellow-400/40 focus:border-yellow-400 focus:outline-none rounded-sm px-2 py-1 text-xs text-yellow-200 font-mono text-center"
            />
            <span className="text-zinc-500">
              → stills map to scenes{" "}
              <span className="text-zinc-200">{descStartScene}</span>
              {files.length > 1 && (
                <>–<span className="text-zinc-200">{Math.min(descStartScene + files.length - 1, parsedDocPrompts.length)}</span></>
              )}
            </span>
          </div>
        </div>
      )}

      {files.length === 0 ? (
        <div className="border border-dashed border-zinc-800 rounded-sm py-12 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            no stills yet · click + Add stills above
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {files.map((file, idx) => (
            <PendingScene
              key={idx}
              idx={idx}
              file={file}
              previewUrl={previews[idx]}
              promptValue={prompts[idx] || ""}
              sourceSceneNumber={
                parsedDocPrompts.length > 0
                  ? descStartScene + idx
                  : null
              }
              onPrompt={(v) => setPromptAt(idx, v)}
              onRemove={() => onRemove(idx)}
              onMove={(dir) => onMove(idx, dir)}
              disabled={submitting}
              isFirst={idx === 0}
              isLast={idx === files.length - 1}
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 justify-end pt-1">
        {submitting && uploadPct > 0 && uploadPct < 100 && (
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
            uploading {uploadPct}%…
          </span>
        )}
        <button
          type="button"
          data-testid="ryo-submit-btn"
          onClick={onSubmit}
          disabled={submitting || files.length === 0}
          className={`font-display text-sm font-black uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors ${
            !submitting && files.length > 0
              ? "bg-yellow-400 text-black hover:bg-yellow-300"
              : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
          }`}
        >
          {submitting
            ? "▸ Uploading…"
            : `▶ Upload ${files.length || ""} scene${files.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </section>
  );
}

function PendingScene({
  idx, file, previewUrl, promptValue, onPrompt,
  sourceSceneNumber = null,
  onRemove, onMove, disabled, isFirst, isLast,
}) {
  return (
    <div
      data-testid={`ryo-pending-${idx}`}
      className="border border-zinc-800 rounded-sm bg-black/40 overflow-hidden"
    >
      <div className="aspect-video bg-zinc-950 relative border-b border-zinc-800">
        {previewUrl && (
          <img
            src={previewUrl}
            alt={`Scene ${idx + 1}`}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute top-2 left-2 font-mono text-[11px] uppercase tracking-[0.2em] bg-yellow-400 text-black px-2 py-0.5 rounded-sm font-bold">
          {sourceSceneNumber ? (
            <>
              SCENE {sourceSceneNumber}
              <span className="text-black/60 ml-1 font-normal">·#{idx + 1}</span>
            </>
          ) : (
            <># {idx + 1}</>
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-1">
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={disabled || isFirst}
            className="font-mono text-[10px] bg-black/80 border border-zinc-700 text-zinc-200 hover:text-yellow-300 disabled:text-zinc-600 px-1.5 py-0.5 rounded-sm"
            data-testid={`ryo-pending-${idx}-up`}
            title="Move up"
          >↑</button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={disabled || isLast}
            className="font-mono text-[10px] bg-black/80 border border-zinc-700 text-zinc-200 hover:text-yellow-300 disabled:text-zinc-600 px-1.5 py-0.5 rounded-sm"
            data-testid={`ryo-pending-${idx}-down`}
            title="Move down"
          >↓</button>
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="font-mono text-[10px] bg-black/80 border border-red-500/50 text-red-300 hover:text-red-200 px-1.5 py-0.5 rounded-sm"
            data-testid={`ryo-pending-${idx}-remove`}
            title="Remove"
          >×</button>
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 truncate">
            {file?.name || ""}
          </div>
          {promptValue?.trim() && (
            <span
              data-testid={`ryo-pending-${idx}-prompt-badge`}
              className="font-mono text-[8px] uppercase tracking-[0.25em] text-yellow-300 border border-yellow-400/40 px-1.5 py-0.5 rounded-sm shrink-0"
              title="Prompt loaded — sent to Veo 3 as this scene's description"
            >
              ● scene description
            </span>
          )}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-yellow-400/80">
          {sourceSceneNumber
            ? `SOURCE SCENE ${sourceSceneNumber} DESCRIPTION · SENT TO VEO 3`
            : `SCENE ${idx + 1} DESCRIPTION · SENT TO VEO 3`}
        </div>
        <textarea
          data-testid={`ryo-pending-${idx}-prompt`}
          rows={3}
          value={promptValue}
          onChange={(e) => onPrompt(e.target.value.slice(0, 1000))}
          placeholder="Describe what should happen in this scene (optional — overrides default). Upload a doc above to auto-fill."
          disabled={disabled}
          className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2 py-1 text-xs text-zinc-100 leading-relaxed resize-y"
        />
      </div>
    </div>
  );
}

// ---------- Job View (click-to-animate) ----------
function JobView({
  job, scenes, channelId, animatingScenes, animateAllBusy,
  pendingAnim, readyCount,
  onAnimate, onAnimateAll, onStopAnimateAll, onStartOver,
  selectedForStitch, onToggleStitchSelection,
  voiceoverFile, onSetVoiceoverFile,
  stitchBusy, onStitch,
}) {
  const total = scenes.length;
  const stitchedStatus = job?.stitched_status || "";
  const stitchedUrl = job?.stitched_video_url || "";
  const stitchedError = job?.stitched_error || "";
  const stitching = stitchBusy || stitchedStatus === "running";
  const stitchReady = stitchedStatus === "ready" && !!stitchedUrl;
  const stitchFailed = stitchedStatus === "failed";
  const fullStitchedUrl = (() => {
    if (!stitchedUrl) return "";
    if (stitchedUrl.startsWith("http")) return stitchedUrl;
    if (stitchedUrl.startsWith("/")) return `${API_BASE}${stitchedUrl}`;
    return stitchedUrl;
  })();
  const stitchSelectedCount = selectedForStitch?.size || 0;

  // Per-scene status counts powering the top progress banner.
  const bannerReady = scenes.filter((s) => s.status === "ready").length;
  const bannerFailed = scenes.filter((s) => s.status === "failed").length;
  const bannerAnimating = scenes.filter(
    (s) => animatingScenes?.has?.(s.idx) || s.status === "animating",
  ).length;
  const bannerQueued = scenes.filter((s) => s.status === "queued").length;
  let bannerStage = "queued";
  if (stitching) bannerStage = "stitching";
  else if (stitchFailed) bannerStage = "failed";
  else if (stitchReady && bannerReady === total) bannerStage = "complete";
  else if (bannerAnimating > 0 || animateAllBusy) bannerStage = "animating";
  else if (bannerReady === total && total > 0) bannerStage = "complete";
  else if (bannerQueued > 0) bannerStage = "queued";

  return (
    <section
      data-testid="ryo-job-view"
      className="border border-zinc-800 rounded-sm p-5 bg-black space-y-5"
    >
      <JobProgressBanner
        stage={bannerStage}
        total={total}
        ready={bannerReady}
        animating={bannerAnimating}
        queued={bannerQueued}
        failed={bannerFailed}
        jobLabel={`Job ${job.job_id}${job.title ? ` · ${job.title}` : ""}`}
        testIdPrefix="ryo-job-progress"
      />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-bold">
            // STEP 2 · ANIMATE
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">
            Job <span className="text-zinc-300">{job.job_id}</span>
            {job.title ? ` · ${job.title}` : ""}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">
            {readyCount}/{total} animated
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {animateAllBusy ? (
            <button
              type="button"
              data-testid="ryo-stop-animate-all"
              onClick={onStopAnimateAll}
              className="font-mono text-[10px] uppercase tracking-[0.25em] bg-red-500/20 border border-red-500/60 text-red-300 hover:bg-red-500/30 px-3 py-2 rounded-sm"
              title="Stop after the current scene finishes animating"
            >
              ■ Stop animating
            </button>
          ) : (
            <button
              type="button"
              data-testid="ryo-animate-all"
              onClick={onAnimateAll}
              disabled={pendingAnim === 0}
              className={`font-display text-sm font-black uppercase tracking-wide px-4 py-2 rounded-sm transition-colors border ${
                pendingAnim > 0
                  ? "bg-fuchsia-500/15 border-fuchsia-400/60 text-fuchsia-200 hover:bg-fuchsia-500/25"
                  : "bg-zinc-900 border-zinc-800 text-zinc-600 cursor-not-allowed"
              }`}
              title={
                pendingAnim === 0
                  ? "All scenes are already animated"
                  : "Send each remaining scene to Veo 3 sequentially (8s clips)"
              }
            >
              ▶▶ Animate All
              {pendingAnim > 0
                ? ` (${pendingAnim})`
                : readyCount > 0
                  ? ` · ALL DONE`
                  : ""}
            </button>
          )}
          <button
            type="button"
            data-testid="ryo-start-over"
            onClick={onStartOver}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-yellow-300 border border-zinc-800 hover:border-yellow-400/50 px-3 py-2 rounded-sm"
            title="Upload a new batch — the existing job stays in your history"
          >
            ⌕ New batch
          </button>
        </div>
      </div>

      <div
        data-testid="ryo-scene-grid"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
      >
        {scenes.map((s) => (
          <JobScene
            key={s.idx}
            scene={s}
            isAnimating={
              animatingScenes.has(s.idx) || s.status === "animating"
            }
            disableAll={animateAllBusy}
            onAnimate={() => onAnimate(s.idx)}
            isSelectedForStitch={selectedForStitch?.has(s.idx)}
            onToggleStitchSelection={() => onToggleStitchSelection?.(s.idx)}
          />
        ))}
      </div>

      {/* Stitch Final Video — visible whenever at least one scene
          has produced a Veo3 clip. Lets the user cherry-pick which
          clips go into the final MP4 and optionally upload a voiceover. */}
      {readyCount > 0 && (
        <StitchPanel
          job={job}
          scenes={scenes}
          selectedCount={stitchSelectedCount}
          voiceoverFile={voiceoverFile}
          onSetVoiceoverFile={onSetVoiceoverFile}
          stitching={stitching}
          stitchReady={stitchReady}
          stitchFailed={stitchFailed}
          stitchedError={stitchedError}
          fullStitchedUrl={fullStitchedUrl}
          onStitch={onStitch}
        />
      )}
    </section>
  );
}

function StitchPanel({
  scenes, selectedCount, voiceoverFile, onSetVoiceoverFile,
  stitching, stitchReady, stitchFailed, stitchedError, fullStitchedUrl,
  onStitch,
}) {
  const readyCount = scenes.filter((s) => s.status === "ready").length;
  return (
    <div
      data-testid="ryo-stitch-panel"
      className="mt-2 border border-zinc-800 rounded-sm p-4 bg-black/40 space-y-3"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-400 font-bold">
            // STEP 3 · STITCH FINAL VIDEO
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">
            <span data-testid="ryo-stitch-selected-count">
              {selectedCount}
            </span>
            /{readyCount} clip{readyCount === 1 ? "" : "s"} selected
            {voiceoverFile ? " · custom voiceover staged" : ""}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            ▸ check the boxes on scene cards to include or exclude them
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label
            data-testid="ryo-voiceover-label"
            className={`font-mono text-[10px] uppercase tracking-[0.25em] cursor-pointer border rounded-sm px-3 py-2 transition-colors ${
              stitching
                ? "opacity-50 cursor-not-allowed border-zinc-800 text-zinc-500"
                : voiceoverFile
                  ? "border-emerald-400/60 text-emerald-300 hover:bg-emerald-500/10"
                  : "border-zinc-700 hover:border-cyan-400 text-zinc-300 hover:text-cyan-300"
            }`}
            title="Optional MP3 / WAV / M4A — muxed on top of the concatenated clips with -shortest"
          >
            {voiceoverFile ? `🎙 ${voiceoverFile.name.slice(0, 24)}` : "🎙 Add voiceover (optional)"}
            <input
              type="file"
              accept="audio/mpeg,audio/mp3,audio/wav,audio/x-m4a,audio/m4a,audio/aac,audio/*"
              className="hidden"
              data-testid="ryo-voiceover-input"
              disabled={stitching}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onSetVoiceoverFile(f);
                e.target.value = "";
              }}
            />
          </label>
          {voiceoverFile && (
            <button
              type="button"
              data-testid="ryo-voiceover-clear"
              onClick={() => onSetVoiceoverFile(null)}
              disabled={stitching}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-red-300 border border-zinc-800 hover:border-red-500/50 px-2 py-2 rounded-sm"
              title="Drop the staged voiceover"
            >
              ×
            </button>
          )}
          <button
            type="button"
            data-testid="ryo-stitch-btn"
            onClick={onStitch}
            disabled={selectedCount === 0 || stitching}
            className={`font-display text-sm font-black uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors ${
              selectedCount > 0 && !stitching
                ? "bg-cyan-400 text-black hover:bg-cyan-300"
                : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
            }`}
          >
            {stitching
              ? "▸ Stitching…"
              : stitchReady
                ? "↻ Re-stitch"
                : `🎬 Stitch ${selectedCount || ""} clip${selectedCount === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>

      {stitching && (
        <div
          data-testid="ryo-stitch-progress"
          className="flex items-center gap-2 text-[11px] text-zinc-300"
        >
          <div className="w-3 h-3 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
          <span>FFmpeg concat + voiceover mux — ~1-2 min per minute of footage…</span>
        </div>
      )}
      {stitchFailed && (
        <div
          data-testid="ryo-stitch-error"
          className="text-[11px] text-red-300 leading-relaxed border border-red-500/30 rounded-sm bg-red-500/5 p-2"
        >
          Stitch failed: {stitchedError || "ffmpeg error"}
        </div>
      )}
      {stitchReady && (
        <>
          <video
            key={fullStitchedUrl}
            src={fullStitchedUrl}
            controls
            playsInline
            preload="metadata"
            data-testid="ryo-stitch-final-video"
            className="w-full aspect-video bg-black rounded-sm border border-cyan-400/40"
          />
          <a
            href={fullStitchedUrl}
            download
            data-testid="ryo-stitch-final-download"
            className="inline-block font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 hover:text-cyan-200 border border-cyan-400/40 px-3 py-1.5 rounded-sm transition-colors"
          >
            ⬇ Download final MP4
          </a>
        </>
      )}
    </div>
  );
}

function JobScene({
  scene, isAnimating, disableAll, onAnimate,
  isSelectedForStitch, onToggleStitchSelection,
}) {
  const url = (raw) => {
    if (!raw) return "";
    if (raw.startsWith("http")) return raw;
    if (raw.startsWith("/")) return `${API_BASE}${raw}`;
    return raw;
  };
  const isReady = scene.status === "ready" && !!scene.video_url;
  const isFailed = scene.status === "failed";
  const canAnimate = !isAnimating && !disableAll;
  const animateLabel = isAnimating
    ? "▸ Animating…"
    : isReady
      ? "↻ Re-animate"
      : isFailed
        ? "↻ Retry"
        : "▶ Animate";

  return (
    <div
      data-testid={`ryo-scene-${scene.idx}`}
      data-status={scene.status}
      className={`border rounded-sm overflow-hidden bg-black/40 transition-colors ${
        isReady ? "border-emerald-400/50"
          : isFailed ? "border-red-500/50"
            : isAnimating ? "border-yellow-400/50"
              : "border-zinc-800"
      }`}
    >
      <div className="aspect-video bg-zinc-950 relative">
        {isReady ? (
          <video
            key={scene.video_url}
            src={url(scene.video_url)}
            controls
            playsInline
            preload="metadata"
            poster={url(scene.image_url)}
            data-testid={`ryo-scene-${scene.idx}-video`}
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            {scene.image_url && (
              <img
                src={url(scene.image_url)}
                alt={`Scene ${scene.idx + 1}`}
                className="w-full h-full object-cover opacity-80"
              />
            )}
            {isAnimating && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60">
                <div className="w-8 h-8 border-2 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin" />
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400">
                  animating · veo 3
                </div>
              </div>
            )}
            {isFailed && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-3 text-center bg-black/70">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-400">
                  failed
                </div>
                <div className="text-[10px] text-zinc-300 line-clamp-3">
                  {scene.error || "Veo 3 failed"}
                </div>
              </div>
            )}
          </>
        )}
        <div className="absolute top-2 left-2 font-mono text-[11px] uppercase tracking-[0.2em] bg-yellow-400 text-black px-2 py-0.5 rounded-sm font-bold">
          # {scene.idx + 1}
        </div>
        {isReady && (
          <label
            className="absolute top-2 right-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] bg-black/85 border border-cyan-400/50 hover:border-cyan-400 text-cyan-200 px-2 py-1 rounded-sm cursor-pointer transition-colors"
            title="Include this clip when stitching the final MP4"
          >
            <input
              type="checkbox"
              data-testid={`ryo-scene-${scene.idx}-stitch-check`}
              checked={!!isSelectedForStitch}
              onChange={onToggleStitchSelection}
              className="w-3 h-3 accent-cyan-400 cursor-pointer"
            />
            stitch
          </label>
        )}
        {isReady && (
          <a
            href={url(scene.video_url)}
            download
            data-testid={`ryo-scene-${scene.idx}-download`}
            className="absolute bottom-2 right-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-emerald-400 text-black hover:bg-emerald-300 px-2 py-0.5 rounded-sm font-bold"
          >
            ⬇ mp4
          </a>
        )}
      </div>

      <div className="p-3 space-y-2">
        {scene.prompt && (
          <div
            className="text-[11px] text-zinc-300 leading-relaxed italic line-clamp-2"
            title={scene.prompt}
          >
            “{scene.prompt}”
          </div>
        )}
        <button
          type="button"
          onClick={onAnimate}
          disabled={!canAnimate}
          data-testid={`ryo-scene-${scene.idx}-animate`}
          className={`w-full font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 rounded-sm transition-colors border ${
            isAnimating
              ? "bg-fuchsia-500/20 border-fuchsia-400/70 text-fuchsia-200 cursor-wait"
              : isReady
                ? "bg-fuchsia-500/10 border-fuchsia-400/50 text-fuchsia-300 hover:bg-fuchsia-500/20"
                : canAnimate
                  ? "bg-fuchsia-500 border-fuchsia-500 text-black hover:bg-fuchsia-400 font-bold"
                  : "bg-zinc-900 border-zinc-800 border-dashed text-zinc-600 cursor-not-allowed"
          }`}
          title={
            !canAnimate && disableAll
              ? "Batch animation running — wait for it to finish"
              : isReady
                ? "Re-animate this scene (replaces the 8s clip)"
                : "Send this still to Veo 3 → 8-second animated clip"
          }
        >
          {animateLabel}
        </button>
      </div>
    </div>
  );
}
